// Headcount and Per Stakeholder Impact Chart Helpers
//
// This module provides configuration and data generation for the Headcount and Per Stakeholder Impact chart.
// It creates a combined bar + line chart with intelligent label positioning that:
// - Dynamically positions labels inside or outside bars based on value size
// - Avoids collisions between Per Stakeholder Impact labels and Headcount labels
// - Ensures labels stay within chart boundaries
// - Adjusts label sizes based on number of data points
//
// Chart structure:
// - Dataset 0: Per Stakeholder Impact (line chart with markers)
// - Dataset 1: Headcount (horizontal bar chart)

import { getCategoricalLabels } from '../SocialValueByStakeholders/socialValueByStakeholdersHelpers'
import { getFilteredReportData } from '../helpers/filterHelpers'
import { CATEGORICAL_IMPACT, STAKEHOLDER_COUNT, IMPACT_PER_STAKEHOLDER } from '../fields'
import {
  checkRangeOverlap,
  measureTextWidth,
  calculateTotalLabelWidth,
  calculateLabelEdges,
  calculateSidePositioningOption,
  selectBestAlignment,
  scorePositioningOption
} from './labelPositioningHelpers'
import { createCollisionCache, manageCacheLifecycle, cachePositioningDecision, getCachedDecision } from './cacheHelpers'

const labelSizes = {
  normal: 'normal',
  small: 'small',
  extraSmall: 'extraSmall'
}

// Style configurations for different label sizes
export const dataLabelsStyles = {
  [labelSizes.normal]: {
    verticalPadding: 2,
    horizontalPadding: 4,
    fontSize: 14,
    offset: 10
  },
  [labelSizes.small]: {
    verticalPadding: 1,
    horizontalPadding: 4,
    fontSize: 12,
    offset: 8
  },
  [labelSizes.extraSmall]: {
    verticalPadding: 0,
    horizontalPadding: 2,
    fontSize: 10,
    offset: 6
  }
}

// Determine label size based on number of data points to prevent crowding
export const getDataLabelsSize = dataCount => {
  if (dataCount > 7) {
    return labelSizes.extraSmall // Many items: smallest labels
  } else if (dataCount > 3) {
    return labelSizes.small // Some items: medium labels
  } else {
    return labelSizes.normal // Few items: large labels
  }
}

// Calculate max values for each dataset separately
export const getMaxValues = (headcountData, perStakeholderData) => {
  const maxHeadcount = headcountData?.length ? Math.max(...headcountData.filter(val => val != null)) : 0
  const maxPerStakeholder = perStakeholderData?.length ? Math.max(...perStakeholderData.filter(val => val != null)) : 0

  return { maxHeadcount, maxPerStakeholder }
}

// Threshold multiplier for determining when to position labels inside vs outside bars
// Values > (maxHeadcount * HEADCOUNT_LABEL_THRESHOLD) are positioned inside the bar
// Set to 1.0 to always position labels outside (no value can exceed 100% of max)
// Set to 0.85 to position labels inside for values >85% of max
const HEADCOUNT_LABEL_THRESHOLD = 0.85

// Create chart datasets with dynamic styling
export const createDatasets = ({
  chartData,
  chartColors,
  dataLabelStyles,
  maxHeadcount,
  maxPerStakeholder,
  getCustomDataLabelsOptions,
  currencySymbol
}) => {
  // Helper to determine if a headcount value should use inside positioning
  const isLargeValue = ctx => {
    const currentValue = ctx.dataset.data[ctx.dataIndex]
    const threshold = maxHeadcount * HEADCOUNT_LABEL_THRESHOLD
    return currentValue > threshold
  }

  // Create cache for sharing collision detection results between align and offset callbacks
  const collisionCache = createCollisionCache()

  return [
    {
      type: 'line',
      label: 'Per Stakeholder Impact',
      data: chartData.perStakeholderData,
      backgroundColor: chartColors.perStakeholderImpact,
      borderColor: chartColors.perStakeholderImpact,
      borderWidth: 3,
      fill: false,
      order: 1,
      xAxisID: 'x-per-stakeholder',
      datalabels: {
        color: 'white',
        backgroundColor: chartColors.perStakeholderImpact,
        borderColor: 'white',
        borderWidth: 2,
        borderRadius: 4,
        padding: {
          top: dataLabelStyles.verticalPadding + 1,
          bottom: dataLabelStyles.verticalPadding + 1,
          right: dataLabelStyles.horizontalPadding + 2,
          left: dataLabelStyles.horizontalPadding + 2
        },
        // Smart horizontal alignment with dynamic positioning to avoid collisions
        align: ctx => {
          const perStakeholderValue = ctx.dataset.data[ctx.dataIndex]
          const headcountValue = chartData.headcountData[ctx.dataIndex]
          const chart = ctx.chart
          const chartArea = chart.chartArea

          // Manage cache lifecycle (clear stale data between render cycles)
          manageCacheLifecycle({ ctx, cache: collisionCache, chartArea })

          // Calculate label positions using Chart.js scales (works on first render)
          const perStakeholderX = chart.scales['x-per-stakeholder'].getPixelForValue(perStakeholderValue)
          const headcountBarEnd = chart.scales['x-headcount'].getPixelForValue(headcountValue)

          // Measure label dimensions
          const perStakeholderText = `${currencySymbol}${perStakeholderValue.toLocaleString()}`
          const perStakeholderTextWidth = measureTextWidth({
            canvasCtx: chart.ctx,
            text: perStakeholderText,
            fontSize: dataLabelStyles.fontSize
          })
          const perStakeholderTotalWidth = calculateTotalLabelWidth(
            perStakeholderTextWidth,
            dataLabelStyles.horizontalPadding
          )

          const headcountText = headcountValue.toLocaleString()
          const headcountTextWidth = measureTextWidth({
            canvasCtx: chart.ctx,
            text: headcountText,
            fontSize: dataLabelStyles.fontSize
          })
          const headcountTotalWidth = calculateTotalLabelWidth(headcountTextWidth, dataLabelStyles.horizontalPadding)

          // Calculate headcount label boundaries
          const headcountIsLarge = headcountValue > maxHeadcount * HEADCOUNT_LABEL_THRESHOLD
          const baseOffset = dataLabelStyles.offset
          const { leftEdge: headcountLeftEdge, rightEdge: headcountRightEdge } = calculateLabelEdges({
            anchorX: headcountBarEnd,
            labelWidth: headcountTotalWidth,
            offset: baseOffset,
            positionLeft: headcountIsLarge
          })

          // Calculate positioning options for LEFT and RIGHT
          const leftOption = calculateSidePositioningOption({
            side: 'left',
            perStakeholderX,
            perStakeholderTotalWidth,
            headcountLeftEdge,
            headcountRightEdge,
            baseOffset,
            chartArea
          })

          const rightOption = calculateSidePositioningOption({
            side: 'right',
            perStakeholderX,
            perStakeholderTotalWidth,
            headcountLeftEdge,
            headcountRightEdge,
            baseOffset,
            chartArea
          })

          // Calculate CENTER option
          const centerLeftEdge = perStakeholderX - perStakeholderTotalWidth / 2
          const centerRightEdge = perStakeholderX + perStakeholderTotalWidth / 2
          const centerCollides = checkRangeOverlap(
            [centerLeftEdge, centerRightEdge],
            [headcountLeftEdge, headcountRightEdge]
          )
          const centerFitsInChart = centerLeftEdge >= chartArea.left && centerRightEdge <= chartArea.right

          // Score options and select best alignment
          const leftScore = scorePositioningOption(leftOption)
          const rightScore = scorePositioningOption(rightOption)
          const { alignment, chosenOption } = selectBestAlignment({
            leftOption,
            rightOption,
            centerCollides,
            centerFits: centerFitsInChart,
            leftScore,
            rightScore
          })

          // Cache decision for offset callback
          cachePositioningDecision({
            cache: collisionCache,
            dataIndex: ctx.dataIndex,
            chosenOption,
            baseOffset,
            alignment
          })

          return alignment
        },
        // Dynamic offset based on chosen positioning decision
        offset: ctx => {
          const cached = getCachedDecision(collisionCache, ctx.dataIndex)

          if (!cached) return dataLabelStyles.offset
          if (!cached.option) return cached.baseOffset

          return cached.option.offset
        },
        anchor: 'center',
        font: {
          size: dataLabelStyles.fontSize,
          weight: 600
        }
      }
    },
    {
      type: 'bar',
      label: 'Headcount',
      data: chartData.headcountData,
      backgroundColor: chartColors.headcount,
      borderRadius: {
        topLeft: 0,
        topRight: 4,
        bottomLeft: 0,
        bottomRight: 4
      },
      borderSkipped: false,
      maxBarThickness: 45,
      order: 2,
      xAxisID: 'x-headcount',
      datalabels: {
        ...getCustomDataLabelsOptions({
          maxValue: maxHeadcount,
          fontSize: dataLabelStyles.fontSize,
          horizontalPadding: dataLabelStyles.horizontalPadding,
          verticalPadding: dataLabelStyles.verticalPadding,
          color: chartColors.headcount
        }),
        // Dynamic positioning based on value size
        // Large values (>threshold): position label INSIDE bar (left of end) to avoid clipping
        // Small values: position label OUTSIDE bar (right of end) for better visibility
        align: ctx => (isLargeValue(ctx) ? 'left' : 'right'),
        // For large values positioned inside, use white text for contrast
        // For small values positioned outside, use bar color
        color: ctx => (isLargeValue(ctx) ? 'white' : chartColors.headcount),
        // Background styling: green for inside labels, white for outside labels
        backgroundColor: ctx => (isLargeValue(ctx) ? chartColors.headcount : 'white'),
        // Border styling: white border for inside labels, bar color border for outside labels
        borderColor: ctx => (isLargeValue(ctx) ? 'white' : chartColors.headcount),
        borderWidth: 3,
        anchor: 'end',
        offset: dataLabelStyles.offset,
        font: {
          size: dataLabelStyles.fontSize,
          weight: 600
        }
      }
    }
  ]
}

// Generate chart data for headcount and per stakeholder impact
export const generateHeadcountData = ({ selectedRegion, report }) => {
  // Note: skipStakeholderFilter is true because headcount chart doesn't support stakeholder filtering
  // Note: fieldName is CATEGORICAL_IMPACT because we need the array of stakeholders
  const categoricalData =
    getFilteredReportData({
      report,
      selectedRegion,
      selectedStakeholder: null,
      fieldName: CATEGORICAL_IMPACT,
      skipStakeholderFilter: true
    }) || []

  const labels = getCategoricalLabels(categoricalData)
  const headcountData = categoricalData.map(item => item[STAKEHOLDER_COUNT] || 0)
  const perStakeholderData = categoricalData.map(item => item[IMPACT_PER_STAKEHOLDER] || 0)

  return {
    labels,
    headcountData,
    perStakeholderData
  }
}
